import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, AutoComplete, Button, Card, Col, Form, Input, Radio, Row, Select, Typography } from 'antd'
import { ExperimentOutlined } from '@ant-design/icons'
import DeductionService from '../../service/DeductionService'
import RecentService from '../../service/RecentService'
import WallexService from '../../service/WallexService'
import { DeductionCreatePayload } from '../../domain/Deduction'
import { useRecentValues } from '../../hooks/useRecent'
import { deductionSample, fillEmpty } from '../../data/samples'
import { paths } from '../../routes'
import { groupThousands } from '../../util/format'
import AmountField from '../ui/AmountField'
import CreatedTicket from '../ui/CreatedTicket'
const { Text } = Typography

const deductionTypeOptions = [
    { value: 'adjustment', label: 'Adjustment' },
    { value: 'refund_to_client_bank_account', label: 'Refund to client bank account' },
    { value: 'bank_fees_inward_credit', label: 'Bank Fees (Inward Credit)' },
    { value: 'bank_fees_others', label: 'Bank Fees (Others)' },
    { value: 'wallex_fees', label: 'Wallex Fees' },
    { value: 'wallex_fees_amendment_charge', label: 'Wallex Fees (Amendment Charge)' },
    { value: 'wallex_fees_change_in_payment_terms', label: 'Wallex Fees (Change in Payment Terms)' },
    { value: 'wallex_fees_fx_losses', label: 'Wallex Fees (FX Losses)' },
    { value: 'wallex_fees_late_funding', label: 'Wallex Fees (Late Funding)' },
    { value: 'wallex_fees_negative_interest_charge', label: 'Wallex Fees (Negative Interest Charge)' },
    { value: 'wallex_fees_payment_cancellation', label: 'Wallex Fees (Payment Cancellation)' },
    { value: 'wallex_fees_recall_charge', label: 'Wallex Fees (Recall Charge)' },
    { value: 'wallex_fees_tracer_charge', label: 'Wallex Fees (Tracer Charge)' },
    { value: 'others', label: 'Other' },
]

const referenceTypeOptions = [
    { value: 'none', label: 'None' },
    { value: 'funding', label: 'Funding' },
    { value: 'payment', label: 'Payment' },
    { value: 'conversion', label: 'Conversion' },
    { value: 'collection', label: 'Collection' },
]

const matchesInput = (input: string, option?: { label?: React.ReactNode }) =>
    String(option?.label ?? '')
        .toLowerCase()
        .includes(input.toLowerCase())

const DeductionCreateForm: React.FC = () => {
    const [form] = Form.useForm<DeductionCreatePayload>()
    const referenceType = Form.useWatch('referenceType', form)
    const userOptions = useRecentValues('userId')
    const navigate = useNavigate()
    const [created, setCreated] = useState<{ id: string; values: DeductionCreatePayload } | null>(null)
    const [submitting, setSubmitting] = useState<boolean>(false)
    const [error, setError] = useState<string | null>(null)
    const hasReference = !!referenceType && referenceType !== 'none'

    const onFinish = (values: DeductionCreatePayload) => {
        // Without a reference type, the remarks double as the reference
        const payload = values.referenceType === 'none' ? { ...values, reference: values.remarks } : values

        setSubmitting(true)
        setError(null)
        DeductionService.create(payload)
            .then((result) => {
                RecentService.record({ kind: 'deduction', id: result.id, label: values.remarks, userId: values.userId })
                setCreated({ id: result.id, values })
            })
            .catch((e) => setError(WallexService.errorDescription(e, 'Couldn’t create the deduction.')))
            .finally(() => setSubmitting(false))
    }

    return (
        <>
            {created && (
                <CreatedTicket
                    noun="deduction"
                    id={created.id}
                    details={[
                        { label: 'Amount', value: `${groupThousands(created.values.amount)} ${created.values.currencyId}` },
                        { label: 'Type', value: deductionTypeOptions.find((option) => option.value === created.values.deductionType)?.label },
                        { label: 'User ID', value: created.values.userId },
                    ]}
                    onUpdateStatus={() => navigate(paths.deduction.update(created.id))}
                    onCreateAnother={() => setCreated(null)}
                />
            )}

            {/* Unmounted while the ticket shows; the form instance keeps its values, so "Create another" starts from the previous ones */}
            {!created && (
                <Form form={form} name="deduction-create" layout="vertical" initialValues={{ referenceType: 'none' }} onFinish={onFinish}>
                    <div className="stack">
                        <div className="form-toolbar">
                            <Text type="secondary">Fields marked * are required.</Text>
                            <Button icon={<ExperimentOutlined />} onClick={() => fillEmpty(form, deductionSample())}>
                                Fill sample data
                            </Button>
                        </div>

                        <Card title="Deduction Information">
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="User ID"
                                        name="userId"
                                        rules={[{ required: true, message: 'Please input User Id' }]}
                                        extra="The user whose balance is reduced"
                                    >
                                        <AutoComplete options={userOptions} filterOption={matchesInput} placeholder="Enter User ID" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <AmountField label="Deduction Amount" currencyName="currencyId" />
                                </Col>
                                <Col xs={24}>
                                    <Form.Item label="Deduction Type" name="deductionType" rules={[{ required: true, message: 'Please input deduction type' }]}>
                                        <Select showSearch optionFilterProp="label" placeholder="Select Deduction Type" options={deductionTypeOptions} />
                                    </Form.Item>
                                </Col>
                                <Col xs={24}>
                                    <Form.Item label="Reference Type" name="referenceType">
                                        <Radio.Group optionType="button" buttonStyle="solid" options={referenceTypeOptions} />
                                    </Form.Item>
                                </Col>
                                {hasReference && (
                                    <Col xs={24}>
                                        <Form.Item label="Reference" name="reference" rules={[{ required: true, message: 'Please input Reference' }]}>
                                            <Input placeholder={`ID of the related ${referenceType}`} />
                                        </Form.Item>
                                    </Col>
                                )}
                                <Col xs={24}>
                                    <Form.Item
                                        label="Remarks"
                                        name="remarks"
                                        rules={[{ required: true, message: 'Please input remarks' }]}
                                        extra={hasReference ? undefined : 'Also used as the reference, since no reference type is selected'}
                                    >
                                        <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {error && <Alert type="error" showIcon message={error} />}

                        <div className="form-actions">
                            <Button type="primary" size="large" htmlType="submit" loading={submitting}>
                                Create Deduction
                            </Button>
                            <Button size="large" onClick={() => form.resetFields()}>
                                Reset
                            </Button>
                        </div>
                    </div>
                </Form>
            )}
        </>
    )
}

export default DeductionCreateForm
